// Trial Subscription Utilities
// Automatically assign 1-month PRO trial to new companies

import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Create a 1-month PRO trial subscription for a new company
 * @param {Object} db - Firestore database instance
 * @param {string} companyId - Company ID
 * @param {string} userId - User ID who created the company
 * @returns {Promise<Object>} - Result of subscription creation
 */
export const createTrialSubscription = async (db, companyId, userId) => {
  try {
    console.log(`🎁 Creating 1-month PRO trial for company: ${companyId}`);
    console.log('📋 Parameters:', { db: !!db, companyId, userId });

    if (!db) {
      throw new Error('Database instance is null or undefined');
    }

    if (!companyId) {
      throw new Error('Company ID is null or undefined');
    }

    if (!userId) {
      throw new Error('User ID is null or undefined');
    }

    const now = new Date();
    const trialEndDate = new Date(now);
    trialEndDate.setDate(trialEndDate.getDate() + 30); // 30 days from now

    const nextResetDate = new Date(trialEndDate);
    
    // Define PRO plan limits
    const proLimits = {
      firebase_operations: 50000,      // 50K operations per month
      storage_mb: 5000                  // 5GB storage
    };

    // Create subscription structure
    const subscriptionData = {
      // Base Plan
      basePlan: {
        planId: 'pro',
        planName: 'PRO Plan (Trial)',
        price: 0,
        limits: proLimits,
        startDate: now,
        nextResetDate: nextResetDate,
        nextBillingDate: trialEndDate,
        validityDays: 30,
        bonusDays: {
          total: 0,
          source: null,
          appliedOn: null
        },
        isTrial: true,
        trialEndsAt: trialEndDate
      },

      // Top-ups (empty initially)
      topups: [],

      // Total Entitlements (same as base plan initially)
      totalEntitlements: { ...proLimits },

      // Current Month Usage (all zeros)
      currentMonthUsage: {
        firebase_operations: 0,
        storage_mb: 0,
        breakdown: {
          basePlan: {
            firebase_operations: 0,
            storage_mb: 0
          },
          topups: []
        }
      },

      // Upgrade History (empty initially)
      upgradeHistory: [],

      // Metadata
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      status: 'active',
      autoRenew: false, // Trial doesn't auto-renew
      
      // Trial specific fields
      isTrialActive: true,
      trialStartDate: now,
      trialEndDate: trialEndDate,
      
      // For legacy compatibility
      plan: 'pro',
      customLimits: proLimits
    };

    // Save to Firestore: companies/{companyId}/subscription_details/subscription
    const subscriptionPath = `companies/${companyId}/subscription_details/subscription`;
    console.log('📄 Writing subscription to path:', subscriptionPath);
    
    const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
    await setDoc(subscriptionRef, subscriptionData);

    console.log(`✅ PRO trial subscription created successfully for ${companyId}`);
    console.log(`📅 Trial ends at: ${trialEndDate.toLocaleDateString()}`);
    console.log('📊 Subscription data written:', {
      planId: subscriptionData.basePlan.planId,
      planName: subscriptionData.basePlan.planName,
      isTrial: subscriptionData.basePlan.isTrial,
      status: subscriptionData.status
    });

    return {
      success: true,
      subscriptionData,
      trialEndDate,
      message: `1-month PRO trial activated until ${trialEndDate.toLocaleDateString()}`
    };

  } catch (error) {
    console.error(`❌ Error creating trial subscription for ${companyId}:`, error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    return {
      success: false,
      error: error.message,
      errorCode: error.code,
      errorName: error.name
    };
  }
};

/**
 * Check if a company's trial has expired
 * @param {Object} subscription - Subscription data
 * @returns {boolean} - True if trial has expired
 */
export const isTrialExpired = (subscription) => {
  if (!subscription || !subscription.isTrialActive || !subscription.trialEndDate) {
    return false;
  }

  const now = new Date();
  const trialEndDate = subscription.trialEndDate.toDate 
    ? subscription.trialEndDate.toDate() 
    : new Date(subscription.trialEndDate);

  return now > trialEndDate;
};

/**
 * Get trial remaining days
 * @param {Object} subscription - Subscription data
 * @returns {number} - Days remaining in trial
 */
export const getTrialRemainingDays = (subscription) => {
  if (!subscription || !subscription.isTrialActive || !subscription.trialEndDate) {
    return 0;
  }

  const now = new Date();
  const trialEndDate = subscription.trialEndDate.toDate 
    ? subscription.trialEndDate.toDate() 
    : new Date(subscription.trialEndDate);

  const diffTime = trialEndDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

/**
 * Convert trial to paid subscription
 * @param {Object} db - Firestore database instance
 * @param {string} companyId - Company ID
 * @param {string} newPlanId - New plan ID (e.g., 'basic', 'pro', 'enterprise')
 * @param {Object} paymentInfo - Payment information
 * @returns {Promise<Object>} - Result of conversion
 */
export const convertTrialToPaid = async (db, companyId, newPlanId, paymentInfo) => {
  try {
    console.log(`🔄 Converting trial to paid subscription for ${companyId}`);

    // This would use the existing upgrade logic from subscriptionUtils.js
    // For now, just mark trial as inactive
    const subscriptionRef = doc(db, `companies/${companyId}/subscription_details/subscription`);
    
    await setDoc(subscriptionRef, {
      isTrialActive: false,
      trialConvertedAt: new Date(),
      trialConvertedTo: newPlanId,
      updatedAt: new Date()
    }, { merge: true });

    console.log(`✅ Trial converted successfully to ${newPlanId}`);

    return {
      success: true,
      message: `Trial converted to ${newPlanId} plan`
    };

  } catch (error) {
    console.error(`❌ Error converting trial for ${companyId}:`, error);
    return {
      success: false,
      error: error.message
    };
  }
};


















